import type { PdfiumApi, Pointer } from "@pdfking/pdfium-wrap"
import type {
  PDFReaderOptions,
  PDFSplitFunction,
  PDFSplitResult,
} from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import type { ReadDocumentResult } from "../functions/ReadDocument"
import { BlobArray } from "@pdfking/compatibility"
import { PdfiumExports as E } from "@pdfking/pdfium-wrap"
import { WithCleanup } from "@pdfking/tfunc"
import { CloseDocument } from "../functions/CloseDocument"
import { EnsurePage } from "../functions/EnsurePage"
import { GetBoxes } from "../functions/GetBoxes"
import { ReadDocument } from "../functions/ReadDocument"
import { PageRange } from "../utils/PageRange"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

const extractPage = async (
  pdfium: PdfiumApi,
  reader: ReadDocumentResult,
  page: number
): Promise<PDFSplitResult> => {
  await EnsurePage(pdfium, reader, page)
  const { doc_pointer } = reader
  const {
    addCallback,
    call,
    malloc,
    readBytes,
    safeCall,
    setInt32,
    writeString,
  } = pdfium

  const new_doc_pointer = await safeCall(E.FPDF_CreateNewDocument)

  const { free: freePageRange, pointer: pageRangePtr } = writeString(`${page}`)

  await safeCall(
    E.FPDF_ImportPages,
    new_doc_pointer,
    doc_pointer,
    pageRangePtr,
    0
  )

  freePageRange()

  const imported_page = await safeCall(E.FPDF_LoadPage, new_doc_pointer, 0)
  const { media_box, trim_box, size } = await GetBoxes(pdfium, imported_page)

  await call(E.FPDFPage_GenerateContent, imported_page)
  await call(E.FPDF_ClosePage, imported_page)

  const chunks: Uint8Array[] = []

  const write_callback = (_: number, data: number, size: number) => {
    chunks.push(readBytes(data as Pointer, size as Bytes))
    return size
  }

  const { free: freeCallback, pointer: callbackPtr } = addCallback(
    write_callback,
    "iiii"
  )
  const { free: freeStruct, pointer: structPtr } = malloc(8 as Bytes)

  setInt32(structPtr, 1)
  setInt32((structPtr + 4) as Pointer, callbackPtr)

  await safeCall(E.FPDF_SaveAsCopy, new_doc_pointer, structPtr, 2)

  freeStruct()
  freeCallback()
  await call(E.FPDF_CloseDocument, new_doc_pointer)

  return {
    blob: BlobArray(chunks, "application/pdf"),
    media_box,
    size,
    trim_box,
  }
}

const splitPages = async (
  api: PdfiumApi,
  reader: ReadDocumentResult,
  rangeFn?: (page_count: number) => Iterable<number>
): Promise<PDFSplitResult[]> => {
  const page_count = await api.call(E.FPDF_GetPageCount, reader.doc_pointer)
  const results: PDFSplitResult[] = []

  for (const page of PageRange(page_count, rangeFn)) {
    results.push(await extractPage(api, reader, page))
  }

  return results
}

async function* PDFSplitImpl(
  api: PdfiumApi,
  input: PDFReaderOptions,
  rangeFn?: (page_count: number) => Iterable<number>
): AsyncGenerator<PDFSplitResult> {
  const reader = await ReadDocument(api, input)

  const results = await WithCleanup(splitPages(api, reader, rangeFn), () => {
    void CloseDocument(api, reader)
  })

  yield* results
}

export const PDFSplit: PDFSplitFunction = (input, rangeFn) =>
  ProvisionPdfium(api => PDFSplitImpl(api, input, rangeFn))
